<template>
    <div>
        <card-header :title="isNew ? 'New Canned Response' : 'Edit Canned Response'" icon="fa-comment-alt-lines"/>

        <card-body>
            <card-list>
                <subheader-list-item icon="fa-align-left" title="Response Details"/>

                <container-list-item padded no-border>
                    <div class="columns">
                        <div class="column">
                            <b-field label-position="inside">
                                <template #label>Title <span class="required-flag">*</span></template>
                                <b-input v-model="form.title"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Domain" label-position="inside">
                                <b-select v-model="form.domain_id" expanded>
                                    <option :value="null">All Domains</option>
                                    <option v-for="d in domains" :key="d.id" :value="d.id">{{d.name}}</option>
                                </b-select>
                            </b-field>
                        </div>
                        <div class="column">
                            <b-field label="Channel Type" label-position="inside">
                                <b-select v-model="form.channel_type" expanded>
                                    <option :value="null">All Types</option>
                                    <option value="email">Email</option>
                                    <option value="sms">SMS</option>
                                    <option value="direct">Direct</option>
                                </b-select>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <b-field label="Category" label-position="inside">
                                <b-input v-model="form.category"></b-input>
                            </b-field>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column">
                            <label class="label is-size-7">Response Body</label>
                            <quill-editor v-model="form.body_html" :options="editorOptions" class="has-background-white content"></quill-editor>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column is-4">
                            <b-field label="Sort Order" label-position="inside">
                                <b-input v-model.number="form.sort_order" type="number"></b-input>
                            </b-field>
                        </div>
                    </div>
                </container-list-item>
            </card-list>
        </card-body>

        <card-footer class="pt-3 pb-3">
            <div class="columns">
                <div class="column">
                    <b-button v-if="!isNew" type="is-warning is-outlined" size="is-small" @click="deleteResponse"><i class="far fa-trash-alt"></i><span class="ml-2" v-if="confirmDelete">Delete Response?</span></b-button>
                </div>
                <div class="column has-text-right">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="submit"
                        :disabled="!form.title"
                    ><i class="fas mr-2" :class="isNew ? 'fa-plus' : 'fa-check'"></i>{{isNew ? 'New Response' : 'Save Response'}}
                    </b-button>
                </div>
            </div>
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardList from "@/TIER/components/CardList";
    import CardFooter from "@/components/common/CardFooter";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import ContainerListItem from "@/TIER/components/ContainerListItem";
    import {client as http} from '@/http_client';

    export default {
        components: {ContainerListItem, SubheaderListItem, CardList, CardFooter, CardBody, CardHeader},
        props: ['card', 'props'],
        data() {
            return {
                form: {
                    domain_id: null,
                    channel_type: null,
                    title: '',
                    body_text: '',
                    body_html: '',
                    category: '',
                    sort_order: 0
                },
                domains: [],
                confirmDelete: false,
                editorOptions: {
                    modules: {
                        toolbar: [
                            ['bold', 'italic', 'underline'],
                            [{'color': []}, {'background': []}],
                            [{'list': 'ordered'}, {'list': 'bullet'}],
                            ['link', 'image'],
                            ['clean']
                        ],
                        keyboard: {
                            bindings: {
                                linebreak: {
                                    key: 13,
                                    shiftKey: true,
                                    handler: function(range) {
                                        this.quill.insertEmbed(range.index, 'break', true, 'user');
                                        this.quill.setSelection(range.index + 1, 'silent');
                                        return false;
                                    }
                                }
                            }
                        }
                    },
                    placeholder: ''
                }
            }
        },
        computed: {
            isNew() {
                return !this.props.responseId || this.props.responseId === 'new';
            }
        },
        methods: {
            loadDomains() {
                http.get('support/domains').then(response => {
                    this.domains = response.data || [];
                }).catch(() => {});
            },
            loadResponse() {
                if (this.isNew) return;
                this.$emit('loading', true);
                http.get('support/canned-responses').then(response => {
                    let found = (response.data || []).find(r => r.id === parseInt(this.props.responseId));
                    if (found) {
                        this.form = {
                            domain_id: found.domain_id || null,
                            channel_type: found.channel_type || null,
                            title: found.title || '',
                            body_text: found.body_text || '',
                            body_html: found.body_html || (found.body_text ? found.body_text.replace(/\n/g, '<br>') : ''),
                            category: found.category || '',
                            sort_order: found.sort_order || 0
                        };
                    }
                    this.$emit('loading', false);
                });
            },
            submit() {
                if (!this.form.title.trim()) return;
                this.$emit('loading', true);
                this.form.body_text = (this.form.body_html || '').replace(/<[^>]+>/g, '').trim();

                let promise = this.isNew
                    ? http.post('support/canned-responses', this.form)
                    : http.put('support/canned-responses/' + this.props.responseId, this.form);

                promise.then(() => {
                    this.$emit('loading', false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    window.alert(err.response?.data?.message || 'An error occurred.');
                    this.$emit('loading', false);
                });
            },
            deleteResponse() {
                if (!this.confirmDelete) {
                    this.confirmDelete = true;
                    setTimeout(() => { this.confirmDelete = false; }, 3000);
                    return;
                }
                this.$emit('loading', true);
                http.delete('support/canned-responses/' + this.props.responseId).then(() => {
                    this.$emit('loading', false);
                    this.$closeCard(this.card);
                }).catch(err => {
                    window.alert(err.response?.data?.message || 'An error occurred.');
                    this.$emit('loading', false);
                });
            }
        },
        mounted() {
            this.loadDomains();
            this.loadResponse();
        }
    }
</script>
